import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@/store';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { useDebugPanel } from '@/context/DebugPanelContext';
import { DocumentDebugService, type IDirectWriteFailure } from '@services/DocumentDebugService';
import type { DirectWriteHandler } from './directWrite.types';
import { DIRECT_WRITE_DEFAULT_REASON, isDirectWriteConfirmRequired } from './directWriteReason';

/**
 * Точечная прямая запись поля мимо обычного сабмита карточки: begin → PATCH → complete → refresh.
 * Общий путь для FormInspector (список панели) и жучка поля (FieldFormulaTooltip), чтобы обе точки
 * входа вели один аудит-стор и одинаково реагировали на лок / ошибку.
 *
 * Отказ бросается как `IDirectWriteFailure` (Error с полями status, lockInfo, conflict, currentHash).
 * Обработчик бросает и тогда, когда прямая запись недоступна в среде.
 */
export function useDirectWrite(): DirectWriteHandler {
    const dispatch = useDispatch<AppDispatch>();
    const { settings, directWriteDebug } = useDebugPanel();
    const directWriteStore = directWriteDebug?.store;
    const openDocId = useTypedSelector((state) => state.document.data?.id);
    const openDocHash = useTypedSelector((state) => state.document.data?.doc_Hash);

    return useCallback<DirectWriteHandler>(
        async (target, value, force = false, reason) => {
            if (!directWriteStore) {
                throw new Error('Прямая запись недоступна в этой среде');
            }
            const explicitReason = reason?.trim();
            // На PROD причина по умолчанию не подходит: её спрашивает подтверждение записи
            if (!explicitReason && isDirectWriteConfirmRequired()) {
                throw new Error('Укажите причину прямой записи');
            }

            // Динамический импорт: не тащить стор-барель (TDZ init-цикл documentCreators) в
            // статический граф жучка — иначе ломается изолированный импорт FormBuilder-стори.
            const refreshDocument = async () => {
                const [{ DocumentActionCreators }, { getCurrentOpenGuid }] = await Promise.all([
                    import('@/store/actionCreators/documentCreators'),
                    import('@/store/actionCreators/documentOpenGuid'),
                ]);
                await dispatch(
                    DocumentActionCreators.fetchDocumentData(
                        target.refreshParentId ?? target.docId,
                        getCurrentOpenGuid(),
                    ),
                );
            };

            // Хэш известен только для открытой карточки; строка связанного документа пишется без проверки
            const expectedHash = !target.foreign && target.docId === openDocId ? openDocHash || undefined : undefined;
            const attemptId = directWriteStore.begin(target, value);
            let failure: IDirectWriteFailure;
            try {
                const response = await DocumentDebugService.writeFields(target.docId, {
                    fields: [{ name: target.fieldName, value }],
                    reason: explicitReason || DIRECT_WRITE_DEFAULT_REASON,
                    source: 'debug-panel',
                    runHooks: settings.directWriteRunHooks,
                    raiseModifyEvents: settings.directWriteRaiseEvents,
                    force,
                    expectedHash,
                });
                if (response.data.success) {
                    directWriteStore.complete(attemptId, 'written');
                    await refreshDocument();
                    return;
                }
                failure = {
                    status: 409,
                    message: response.data.message || 'Документ занят другим пользователем',
                    lockInfo: response.data.lockInfo,
                    conflict: response.data.conflict,
                    currentHash: response.data.currentHash,
                };
            } catch (error) {
                failure = DocumentDebugService.getFailure(error);
            }

            const changed = failure.status === 409 && failure.conflict === 'changed';
            directWriteStore.complete(attemptId, failure.status === 409 && !changed ? 'locked' : 'error', {
                message: failure.message,
                lockInfo: failure.lockInfo,
            });
            // Документ сохранили после показа «было» — перечитать, чтобы подтверждение показало актуальное
            if (changed) await refreshDocument();
            throw Object.assign(new Error(failure.message), failure);
        },
        [
            directWriteStore,
            dispatch,
            openDocHash,
            openDocId,
            settings.directWriteRaiseEvents,
            settings.directWriteRunHooks,
        ],
    );
}
